import { useEffect, useState, ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Pie } from 'react-chartjs-2';
import {
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { dashboardApi } from '../api/endpoints';
import { Button } from '../components/ui/Button';
import { formatFileSize, getStatusColor, getStatusText } from '../utils/helpers';
import TaskDetailPanel from '../components/TaskDetailPanel';
import SpotlightCard from '../components/ui/SpotlightCard';
import { InteractiveList } from '../components/ui/PhysicsList';
import PageHeader from '../components/ui/PageHeader';
import { ChartSkeleton } from '../components/ui/SkeletonPatterns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

interface Task {

  id: string;
  user_id: string;
  file_name: string;
  file_size: number;
  status: string;
  created_at: number;
}

interface DashboardStats {
  tasks: {
    total: number;
    completed: number;
    failed: number;
    queued: number;
    today: number;
  };
  instances: {
    activeCount: number;
    totalCount: number;
  };
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentTasks, setRecentTasks] = useState<Task[]>([]);
  const [trends, setTrends] = useState<Array<{ date: string; completed: number; failed: number; total: number }>>([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [loadingTrends, setLoadingTrends] = useState(true);
  const [error, setError] = useState('');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null);
  const cacheDuration = 2 * 60 * 1000; // 2 minutes cache for dashboard

  const isCacheValid = () => {
    if (!lastFetchTime) return false;
    return Date.now() - lastFetchTime < cacheDuration;
  };

  useEffect(() => {
    const cacheIsValid = isCacheValid();
    const hasCachedData = stats !== null && recentTasks.length > 0 && trends.length > 0;

    if (cacheIsValid && hasCachedData) {
      setLoadingStats(false);
      setLoadingTasks(false);
      setLoadingTrends(false);
      const interval = setInterval(loadAllData, 30000);
      return () => clearInterval(interval);
    }

    loadAllData();
    const interval = setInterval(loadAllData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadAllData = () => {
    loadStats();
    loadRecentTasks();
    loadTrends();
    setLastFetchTime(Date.now());
  };

  const loadStats = async () => {
    try {
      setLoadingStats(true);
      setError('');
      const res = await dashboardApi.getStats();
      if (res.success && res.data) setStats(res.data);
    } catch (err) {
      setError('加载统计数据失败');
      console.error('Stats load error:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadRecentTasks = async () => {
    try {
      setLoadingTasks(true);
      setError('');
      const res = await dashboardApi.getRecentTasks(10);
      if (res.success && res.data) setRecentTasks(res.data.tasks || []);
    } catch (err) {
      setError('加载最近任务失败');
      console.error('Tasks load error:', err);
    } finally {
      setLoadingTasks(false);
    }
  };

  const loadTrends = async () => {
    try {
      setLoadingTrends(true);
      setError('');
      const res = await dashboardApi.getTrends(7);
      if (res.success && res.data) setTrends(res.data.trends || []);
    } catch (err) {
      setError('加载趋势数据失败');
      console.error('Trends load error:', err);
    } finally {
      setLoadingTrends(false);
    }
  };

  const trendData = useMemo(() => ({
    labels: trends.map((_, index) => {
      if (index === trends.length - 1) return '今天';
      if (index === trends.length - 2) return '昨天';
      return `${trends.length - 1 - index}天前`;
    }),
    datasets: [
      {
        label: '成功',
        data: trends.map(trend => trend.completed),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.05)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#10b981',
        pointBorderColor: 'rgba(255, 255, 255, 0.1)',
      },
      {
        label: '失败',
        data: trends.map(trend => trend.failed),
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#ef4444',
        pointBorderColor: 'rgba(255, 255, 255, 0.2)',
      },
    ],
  }), [trends]);

  const statusData = useMemo(() => stats
    ? {
        labels: ['已完成', '失败', '排队中'],
        datasets: [
          {
            data: [stats.tasks.completed, stats.tasks.failed, stats.tasks.queued],
            backgroundColor: [
              'rgba(16, 185, 129, 0.8)',
              'rgba(244, 63, 94, 0.8)',
              'rgba(245, 158, 11, 0.8)',
            ],
            borderColor: [
              '#10b981',
              '#f43f5e',
              '#f59e0b',
            ],
            borderWidth: 0,
          },
        ],
      }
    : null, [stats]);

  const handleViewDetails = (task: Task) => {
    if (selectedTask?.id === task.id) {
      setIsDetailOpen(false);
      setSelectedTask(null);
    } else {
      setSelectedTask(task);
      setIsDetailOpen(true);
    }
  };

   return (
     <div className="flex w-full overflow-hidden">
       <div className="flex-1 min-w-0 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]">
           <PageHeader
             title="仪表盘"
             actions={
               <Button variant="default" onClick={loadAllData}>
                 <ArrowPathIcon className={`w-4 h-4 ${(loadingStats || loadingTasks || loadingTrends) ? 'animate-spin' : ''}`} />
                 刷新
               </Button>
             }
           />

        {error && (
          <div className="p-4 mb-6 text-sm text-red-400 bg-red-500/10 ring-1 ring-red-500/20 rounded-xl">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {loadingStats && !stats ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white/5 rounded-2xl p-4 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="h-3 bg-white/10 rounded w-1/3 mb-2 sm:mb-3 animate-pulse" />
                      <div className="h-8 sm:h-10 bg-white/10 rounded w-1/2 animate-pulse" />
                    </div>
                    <div className="p-2.5 sm:p-3 rounded-xl bg-white/10 shrink-0">
                      <div className="w-8 h-8 bg-white/10 rounded animate-pulse" />
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : stats ? (
            <>
              <StatCard
                title="总任务数"
                value={stats.tasks.total}
                icon={<ClipboardDocumentListIcon className="w-8 h-8" />}
                iconColor="text-sky-400 bg-sky-500/10"
                isHighlighted
                onClick={() => navigate('/tasks')}
              />
              <StatCard
                title="今日任务"
                value={stats.tasks.today}
                icon={<ClockIcon className="w-8 h-8" />}
                iconColor="text-amber-400 bg-amber-500/10"
                isDimmed
                onClick={() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  navigate(`/tasks?startDate=${today.getTime()}`);
                }}
              />
              <StatCard
                title="成功任务"
                value={stats.tasks.completed}
                icon={<CheckCircleIcon className="w-8 h-8" />}
                iconColor="text-emerald-400 bg-emerald-500/10"
                isDimmed
                onClick={() => navigate('/tasks?status=completed')}
              />
              <StatCard
                title="失败任务"
                value={stats.tasks.failed}
                icon={<XCircleIcon className="w-8 h-8" />}
                iconColor="text-red-400 bg-red-500/10"
                isDimmed
                onClick={() => navigate('/tasks?status=failed')}
              />
            </>
          ) : null}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <SpotlightCard variant="flat" className="p-6 shadow-xl min-h-[350px] backdrop-blur-md" enablePhysics={true}>
            <h2 className="text-lg font-semibold text-white mb-4">
              任务趋势（近 7 天）
            </h2>
            {loadingTrends && trends.length === 0 ? (
              <ChartSkeleton />
            ) : (
              <div className="relative h-64 w-full">
                <Line data={trendData} options={chartOptions as any} />
              </div>
            )}
          </SpotlightCard>

          <SpotlightCard variant="flat" className="p-6 shadow-xl min-h-[350px] backdrop-blur-md" enablePhysics={true}>
            <h2 className="text-lg font-semibold text-white mb-4">
              任务状态分布
            </h2>
            {loadingStats && !stats ? (
              <ChartSkeleton />
            ) : (
              <div className="relative h-64 w-full flex items-center justify-center">
                {statusData ? (
                  <Pie data={statusData} options={pieChartOptions as any} />
                ) : (
                  <div className="text-gray-500 dark:text-gray-400">
                    暂无任务数据
                  </div>
                )}
              </div>
            )}
          </SpotlightCard>
        </div>

        <SpotlightCard variant="flat" className="overflow-hidden shadow-xl mb-8 backdrop-blur-md" enablePhysics={true} sensitivity={0.2}>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white">
              最近任务
            </h2>
          </div>
            <InteractiveList
              items={recentTasks}
              loading={loadingTasks}
              emptyMessage="暂无任务"
              selectedItemId={selectedTask?.id}
              onItemClick={handleViewDetails}
              spacing="compact"
              renderItem={(task) => (
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium transition-colors truncate ${selectedTask?.id === task.id ? 'text-white' : 'text-white/90'}`}>
                    {task.file_name}
                  </p>
                  <p className="text-xs text-white/50 mt-1">
                    用户: {task.user_id} · {formatFileSize(task.file_size)}
                  </p>
                </div>
                <div className="ml-4">
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(
                      task.status
                    )}`}
                  >
                    {getStatusText(task.status)}
                  </span>
                </div>
              </div>
            )}
          />
        </SpotlightCard>
      </div>

      <TaskDetailPanel 
        isOpen={isDetailOpen} 
        onClose={() => setIsDetailOpen(false)} 
        task={selectedTask} 
      />
    </div>
  );
}

function StatCard({ title, value, icon, iconColor, isHighlighted, isDimmed, onClick }: { title: string; value: number; icon: ReactNode; iconColor?: string; isHighlighted?: boolean; isDimmed?: boolean; onClick?: () => void }) {
  return (
    <SpotlightCard
      variant="flat"
      className={`group p-4 sm:p-6 backdrop-blur-md transition-colors ${onClick ? 'cursor-pointer hover:bg-white/5 active:scale-[0.98]' : ''}`}
      style={isDimmed ? { opacity: 0.7 } : {}}
      enablePhysics={true}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className={`text-xs sm:text-sm font-medium ${isHighlighted ? 'text-white/60' : 'text-white/40'} truncate`}>
            {title}
          </p>
          <p className="mt-1 sm:mt-2 text-2xl sm:text-3xl font-bold text-white tracking-tight">
            {value}
          </p>
        </div>
        <div
          className={`p-2.5 sm:p-3 rounded-xl shrink-0 ${iconColor || 'text-white/70 bg-white/10'}`}
        >
          {icon}
        </div>
      </div>
    </SpotlightCard>
  );
}

  const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top' as const,
      labels: {
        color: 'rgba(255, 255, 255, 0.7)',
        font: {
          size: 12
        }
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(255, 255, 255, 0.1)',
      },
      ticks: {
        color: 'rgba(255, 255, 255, 0.7)',
      },
    },
    x: {
      grid: {
        color: 'rgba(255, 255, 255, 0.1)',
      },
      ticks: {
        color: 'rgba(255, 255, 255, 0.7)',
      },
    },
  },
};

const pieChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        color: 'rgba(255, 255, 255, 0.7)',
        font: {
          size: 12
        }
      },
    },
  },
};